// Staff two-step login (Phase 1 quotation G20) — typed client for
// /api/auth/mfa/*. During sign-in there is no session yet, so those calls carry
// the short-lived `mfa_token` from /auth/login in the body instead.
import { api } from '@/lib/api/client';
import type { SessionUser } from '@/lib/api/session';

export interface TwoStepStatus {
  mfa_enabled: boolean;
  mfa_enabled_at: string | null;
  /** The user's role requires two-step (it can't be turned off). */
  required: boolean;
  recovery_codes_left: number;
}

export interface SetupInfo {
  secret: string;
  otpauth_uri: string;
}

export interface SessionResult {
  token: string;
  user: SessionUser;
}

/** One of the two ways to prove you hold the phone (or the printed codes). */
export type CodeProof = { code: string } | { recovery_code: string };

export const fetchTwoStepStatus = () => api.get<TwoStepStatus>('/auth/mfa/status');

export const beginSetup = (mfaToken?: string) =>
  api.post<SetupInfo>('/auth/mfa/setup', mfaToken ? { mfa_token: mfaToken } : {});

/** During sign-in the reply also carries the session. */
export const confirmSetup = (code: string, mfaToken?: string) =>
  api.post<{ recovery_codes: string[] } & Partial<SessionResult>>('/auth/mfa/enable', { code, ...(mfaToken ? { mfa_token: mfaToken } : {}) });

export const verifySignIn = (mfaToken: string, proof: CodeProof) =>
  api.post<SessionResult & { recovery_codes_left: number }>('/auth/mfa/verify', { mfa_token: mfaToken, ...proof });

export const regenerateRecoveryCodes = (proof: CodeProof) =>
  api.post<{ recovery_codes: string[] }>('/auth/mfa/recovery-codes', proof);

export const disableTwoStep = (proof: CodeProof) => api.post<{ ok: true }>('/auth/mfa/disable', proof);

/** Admin: turn two-step off for someone who lost their phone (signs them out). */
export const resetUserTwoStep = (userId: number, reason: string) =>
  api.post<{ ok: true }>(`/users/${userId}/mfa/reset`, { reason });

/** "123 456" / "123-456" → "123456"; recovery codes keep letters and the dash. */
export const cleanCode = (raw: string) => raw.replace(/[^0-9]/g, '').slice(0, 6);
